import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { AlertCircle, Search } from "tabler-icons-react";
import { analyzePhoneNumbers } from "../../utils/analyzePhoneNumbers";

const PhoneAnalyzer = () => {
  const [phoneNumbers, setPhoneNumbers] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalyze = async () => {
    setError(null);
    setResults(null);

    // Extraire les numéros (un par ligne ou séparés par virgule)
    const numbers = phoneNumbers
      .split(/[\n,;]/)
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    if (numbers.length === 0) {
      setError("Veuillez entrer au moins un numéro de téléphone");
      return;
    }

    setLoading(true);
    try {
      const stats = await analyzePhoneNumbers(numbers);
      setResults(stats);
    } catch (err) {
      setError("Erreur lors de l'analyse: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p="xl">
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack spacing="md">
          <Title order={2}>Analyseur de Numéros de Téléphone</Title>

          <Text size="sm" color="dimmed">
            Entrez les numéros de téléphone (un par ligne ou séparés par des
            virgules)
          </Text>

          <Textarea
            placeholder="0781234567&#10;0782345678&#10;0783456789"
            value={phoneNumbers}
            onChange={(e) => setPhoneNumbers(e.target.value)}
            minRows={8}
            maxRows={15}
          />

          <Button
            onClick={handleAnalyze}
            loading={loading}
            leftIcon={<Search size={16} />}
            disabled={!phoneNumbers.trim()}
          >
            Analyser
          </Button>

          {error && (
            <Alert icon={<AlertCircle size={16} />} title="Erreur" color="red">
              {error}
            </Alert>
          )}

          {loading && (
            <Group position="center" p="xl">
              <Loader size="lg" />
              <Text>Analyse en cours...</Text>
            </Group>
          )}

          {results && (
            <Stack spacing="xl" mt="md">
              {/* Résumé */}
              <Card withBorder>
                <Stack spacing="sm">
                  <Title order={3}>Résumé</Title>
                  <Group spacing="lg">
                    <Badge size="lg" color="blue">
                      Total: {results.total}
                    </Badge>
                    {results.notFound > 0 && (
                      <Badge size="lg" color="red">
                        Non trouvés: {results.notFound}
                      </Badge>
                    )}
                  </Group>
                </Stack>
              </Card>

              {/* Par Type */}
              <Card withBorder>
                <Stack spacing="sm">
                  <Title order={4}>Répartition par Type</Title>
                  <Group spacing="md">
                    {Object.entries(results.byType).map(([type, count]) => (
                      <Badge
                        key={type}
                        size="lg"
                        color={
                          type === "CLIENT"
                            ? "green"
                            : type === "PROSPECT"
                              ? "blue"
                              : type === "Utilisateur"
                                ? "violet"
                                : type === "CONTACT"
                                  ? "orange"
                                  : "gray"
                        }
                        variant="filled"
                      >
                        {type}: {count}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </Card>

              {/* Par Provenance */}
              <Card withBorder>
                <Stack spacing="sm">
                  <Title order={4}>Répartition par Provenance</Title>
                  <Group spacing="md">
                    {Object.entries(results.byProvenance).map(
                      ([provenance, count]) => (
                        <Badge
                          key={provenance}
                          size="lg"
                          color={
                            provenance === "Genimi"
                              ? "violet"
                              : provenance === "Senassurance"
                                ? "cyan"
                                : "gray"
                          }
                          variant="light"
                        >
                          {provenance}: {count}
                        </Badge>
                      ),
                    )}
                  </Group>
                </Stack>
              </Card>

              {/* Détails */}
              <Card withBorder>
                <Stack spacing="sm">
                  <Title order={4}>Détails des Parents</Title>
                  <Box sx={{ overflowX: "auto" }}>
                    <Table striped highlightOnHover>
                      <thead>
                        <tr>
                          <th>Téléphone</th>
                          <th>Nom complet</th>
                          <th>Type</th>
                          <th>Provenance</th>
                          <th>Crédits</th>
                          <th>Enfants</th>
                          <th>Classes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.details.map((parent, index) => (
                          <tr key={index}>
                            <td>{parent.phone}</td>
                            <td>{parent.fullName}</td>
                            <td>
                              <Badge
                                color={
                                  parent.typeAffiche === "CLIENT"
                                    ? "green"
                                    : parent.typeAffiche === "PROSPECT"
                                      ? "blue"
                                      : parent.typeAffiche === "Utilisateur"
                                        ? "violet"
                                        : "orange"
                                }
                                size="sm"
                              >
                                {parent.typeAffiche}
                              </Badge>
                            </td>
                            <td>{parent.provenance || "-"}</td>
                            <td>{parent.credits || 0}</td>
                            <td>{parent.countEnfant || 0}</td>
                            <td>
                              <Text
                                size="sm"
                                color={
                                  parent.classesEnfants === "Aucune classe"
                                    ? "dimmed"
                                    : "dark"
                                }
                              >
                                {parent.classesEnfants}
                              </Text>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Box>
                </Stack>
              </Card>

              {/* Numéros non trouvés */}
              {results.notFound > 0 && (
                <Card withBorder>
                  <Stack spacing="sm">
                    <Title order={4} color="red">
                      Numéros non trouvés ({results.notFound})
                    </Title>
                    <Text size="sm" color="dimmed">
                      {results.notFoundNumbers.join(", ")}
                    </Text>
                  </Stack>
                </Card>
              )}
            </Stack>
          )}
        </Stack>
      </Card>
    </Box>
  );
};

export default PhoneAnalyzer;
